import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, computed, effect, inject, input, output, signal } from '@angular/core';
import { AudioService } from '@core/audio/audio.service';
import { HudButtonComponent } from '@features/hud/hud-button/hud-button.component';
import { LpActionsService } from '@features/lp/lp-actions/lp-actions.service';
import { LpCharacterComponent } from '@features/lp/lp-character/lp-character.component';
import { LpPlaceableMediaComponent } from '@features/lp/lp-placeable-media/lp-placeable-media.component';
import { LpTextComponent } from '@features/lp/lp-text/lp-text.component';
import { MODAL_ANIMATION } from '@features/styles/animations';
import { StyleSheetSpriteComponent } from '@features/styles/style-sheet-sprite/style-sheet-sprite.component';
import { StylesService } from '@features/styles/styles.service';
import { EASE_OUT_BACK } from '@shared/css.utils';
import { waitForMillis } from '@shared/delay.utils';
import { LpCharacter, LpEmotion, LpPlaceableMedia, LpStory, LpStoryOption } from '@typings/lp/generated';

@Component({
  selector: 'lp-story',
  imports: [LpCharacterComponent, StyleSheetSpriteComponent, LpTextComponent, HudButtonComponent, LpPlaceableMediaComponent],
  templateUrl: './lp-story.component.html',
  styleUrl: './lp-story.component.css',
  animations: [
    MODAL_ANIMATION(false),
    trigger('typingDone', [
      state('TYPING', style({ scale: '0' })),
      state('DONE', style({ scale: '1' })),
      transition('TYPING => DONE', [animate(`0.25s ${EASE_OUT_BACK}`)]),
      transition('DONE => TYPING', [animate(`0.125s ease-out`)]),
    ]),
    trigger('talk', [
      transition('talk1 <=> talk2, :enter', [
        animate(
          1000,
          keyframes([
            style({ transform: 'rotate(0deg)', offset: 0 }),
            style({ transform: 'rotate(1deg) translate(0, 8px)', offset: 0.2 }),
            style({ transform: 'rotate(-1deg) translate(0, 0)', offset: 0.4 }),
            style({ transform: 'rotate(1deg) translate(0, 8px)', offset: 0.6 }),
            style({ transform: 'rotate(-1deg) translate(0, 0)', offset: 0.8 }),
            style({ transform: 'rotate(0deg)', offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
})
export class LpStoryComponent implements OnInit {
  readonly #lpActionsService = inject(LpActionsService);
  readonly #audioService = inject(AudioService);
  readonly _stylesService = inject(StylesService);

  story = input.required<LpStory>();

  isTalking = signal(false);
  isPlayingAudio = signal(false);
  isPlayingBackground = signal(false);
  doAnimateTyping = signal(true);
  lineIndex = signal<number>(0);
  line = computed(() => {
    const lineIndex = this.lineIndex();
    if (lineIndex === -1) return undefined;

    return this.story().generalStoryLines.at(lineIndex);
  });
  character = signal<LpCharacter | undefined>(undefined);
  characterEmotion = signal<LpEmotion>('Neutral');
  background = signal<LpPlaceableMedia | undefined>(undefined);
  finished = output();
  talkTrigger = signal<'TALK1' | 'TALK2'>('TALK1'); // TODO: weird way to retigger the animation
  isLineFinished = signal(false);
  autoContinue = signal(false);
  autoContinueDelay = signal(0);

  constructor() {
    // when the story changes, reset the index
    effect(() => {
      this.story(); // watch story change

      this.lineIndex.set(0);
    });

    // when the current line changes, apply the options
    effect(() => {
      const line = this.line();
      queueMicrotask(async () => {
        this.isLineFinished.set(false);
        this.isPlayingAudio.set(true); // NOTE: we mark the audio as playing, so that we won't accidentally finish the line when the audio hasn't started yet, but the options are still being applied.

        // apply options
        for (const option of line?.options ?? []) {
          await this.applyOption(option);
        }

        // start audio
        if (line?.text.audio) {
          await this.#audioService.play(line.text.audio.url).waitForEnd();
        }
        this.isPlayingAudio.set(false);

        this.tryFinishLine();
      });
    });

    effect(async () => {
      if (this.isLineFinished()) {
        if (this.autoContinue()) {
          await waitForMillis(this.autoContinueDelay());
          this.next();
        }
      }
    });
  }

  ngOnInit() {
    const startingCharacter = this.story().startingCharacters?.[0];
    if (startingCharacter) {
      this.character.set(startingCharacter);
      this.triggerTalkAnimation();
    }
  }

  onStartedTyping() {
    this.isTalking.set(true);
  }

  onFinishedTyping() {
    this.doAnimateTyping.set(true); // the next line will animate (again)
    this.isTalking.set(false);

    this.tryFinishLine();
  }

  onFinishedBackground() {
    this.isPlayingBackground.set(false);
    this.tryFinishLine();
  }

  private tryFinishLine() {
    if (this.isPlayingAudio()) return;
    if (this.isPlayingBackground()) return;
    if (this.isTalking()) return;

    this.isLineFinished.set(true);
  }

  onClick() {
    if (this.lineIndex() === -1) return;

    if (this.isTalking()) {
      this.doAnimateTyping.set(false);
    } else if (!this.autoContinue()) {
      this.next();
    }
  }

  // NOTE: we use the onModalDone listener as a hook for the finished output, so that the modal is closed before the finished event is emitted, so that it has time to finish the animation.
  onModalDone() {
    if (this.lineIndex() === -1) this.finished.emit();
  }

  next() {
    const nextIndex = this.lineIndex() + 1;
    if (nextIndex >= this.story().generalStoryLines.length) {
      this.lineIndex.set(-1);
      // NOTE: when there's no modal (ie. the text is empty and auto continue is true (because then there's no text box animation, and also no 'ga verder' button animation)), we immediately emit the finished event
      if ((this.line()?.text.text.length ?? 0) === 0 && this.autoContinue()) {
        this.finished.emit();
      }
      return;
    }

    this.lineIndex.set(nextIndex);
    const doesLineHaveText = (this.line()?.text.text.length ?? 0) > 0;
    if (doesLineHaveText) this.triggerTalkAnimation();
  }

  triggerTalkAnimation() {
    this.talkTrigger.set(this.talkTrigger() === 'TALK1' ? 'TALK2' : 'TALK1');
  }

  async applyOption(option: LpStoryOption) {
    // NOTE: try to keep the order of the if statements

    if (option.dataType === 'RunActionsStoryOption') {
      await this.#lpActionsService.perform(option.actions);
      return;
    }

    if (option.dataType === 'RemoveCharacterFromStageStoryOption') {
      this.character.set(undefined);
      this.characterEmotion.set('Neutral');
      return;
    }

    if (option.dataType === 'SetAutoContinueStoryOption') {
      this.autoContinue.set(option.autoContinue);
      this.autoContinueDelay.set(option.delay * 1000);
      return;
    }

    if (option.dataType === 'SetBackgroundStoryOption') {
      this.background.set(option.background);
      if (option.background.dataType === 'VideoPlaceableMedia') {
        this.isPlayingBackground.set(true);
      }
      return;
    }

    if (option.dataType === 'SetEmotionStoryOption') {
      this.characterEmotion.set(option.emotion);
      return;
    }

    if (option.dataType === 'SetSpeakingCharacterStoryOption') {
      this.character.set(option.character);
      return;
    }

    console.error('Unknown option', option);
    throw new Error(`Option '${option.dataType}' is not supported`);
  }
}
